/**
 * Pacote 4, bloco 3 (rev4 aprovada em 17/10/2026) — blocos compartilhados das fichas de Ordem (OCF e OL):
 * cabeçalho compacto, acompanhamento por etapas, quadros na horizontal (Parceiro/Inttrix), quadros de local
 * simétricos (Faturamento/Entrega), faixa de condições, linhas de seção e recolhíveis. Componentes reais do HUB.
 */
import type { ReactNode } from 'react';
import { dataBr } from '../../../backend/src/compartilhado/oportunidades.ts';
import { Icone, type NomeIcone } from '../componentes/icones.tsx';
import { Ajuda } from './ui.tsx';
import type { ValorHistorico } from './Ordens.tsx';

export function CabecalhoOrdem({ numero, tipo, status, contexto, acoes, teste }: { numero: ReactNode; tipo: string; status: ReactNode; contexto: ReactNode[]; acoes: ReactNode; teste?: string }) {
  return (
    <section className="resumo-destaque cab-ordem" data-teste={teste}>
      <h1 className="titulo-pagina">
        {numero} <span className="tipo-ordem">· {tipo}</span>
      </h1>
      <div className="acoes-cab">
        {status}
        {acoes}
      </div>
      <p className="contexto-ordem" data-teste="origem-ordem">
        {contexto.filter(Boolean).map((c, i) => (
          <span key={i}>
            {i > 0 && <span className="sep"> · </span>}
            {c}
          </span>
        ))}
      </p>
    </section>
  );
}

export interface Etapa {
  nome: string;
  sub: ReactNode;
  estado: 'concluida' | 'atual' | 'pendente';
  extra?: ReactNode;
  teste?: string;
}
export function Passos({ titulo, etapas, acao, teste }: { titulo: string; etapas: Etapa[]; acao?: ReactNode; teste?: string }) {
  return (
    <section className="passos-cartao" data-teste={teste} aria-label={titulo}>
      <div className="cab-passos">
        <h2>{titulo}</h2>
        {acao && <div className="acao-passos">{acao}</div>}
      </div>
      <ol className="passos">
        {etapas.map((e, i) => (
          <li key={e.nome} className={`passo passo-${e.estado}`} data-teste={e.teste} aria-current={e.estado === 'atual' ? 'step' : undefined}>
            <span className="num" aria-hidden="true">
              {e.estado === 'concluida' ? '✓' : i + 1}
            </span>
            <div className="txt">
              <strong>{e.nome}</strong>
              <span className="sub">{e.sub}</span>
              {e.extra && <span className="extra">{e.extra}</span>}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Quadro com os campos lado a lado (Parceiro e Inttrix). */
export function QuadroCampos({ titulo, icone, campos, tom = 'turquesa', teste, acao }: { titulo: string; icone: NomeIcone; campos: [string, ReactNode][]; tom?: 'turquesa' | 'neutro'; teste?: string; acao?: ReactNode }) {
  return (
    <section className={`quadro-h tom-${tom}`} data-teste={teste}>
      <h3>
        <Icone nome={icone} /> {titulo}
        {acao}
      </h3>
      <dl className={`campos-h n${campos.length}`}>
        {campos.map(([r, v]) => (
          <div key={r}>
            <dt>{r}</dt>
            <dd className={r === 'CNPJ' ? 'nowrap' : undefined}>{v || <span className="ausente">—</span>}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Quadro de local (Faturamento / Entrega): razão social e as linhas completas, com a mesma densidade dos dois lados. */
export function QuadroLocal({ titulo, icone, razao, linhas, tom, marca, acao, teste }: { titulo: string; icone: NomeIcone; razao: ReactNode; linhas: ReactNode[]; tom: 'turquesa' | 'azul'; marca?: string; acao?: ReactNode; teste?: string }) {
  return (
    <section className={`quadro-local tom-${tom}`} data-teste={teste}>
      <h3>
        <Icone nome={icone} /> {titulo}
        {marca && <span className="selo selo-neutro">{marca}</span>}
        {acao}
      </h3>
      <p className="razao">{razao}</p>
      {linhas.filter(Boolean).map((l, i) => (
        <p key={i}>{l}</p>
      ))}
    </section>
  );
}

export const Par = ({ children }: { children: ReactNode }) => <div className="par-quadros">{children}</div>;

export function FaixaCondicoes({ titulo, itens, teste }: { titulo: string; itens: [string, ReactNode][]; teste?: string }) {
  return (
    <section className="faixa-cond" data-teste={teste}>
      <Icone nome="fatura" />
      <strong>{titulo}</strong>
      {itens.map(([r, v], i) => (
        <span key={r} className="item">
          {i > 0 && <span className="barra" aria-hidden="true" />}
          <span className="r">{r}:</span> {v}
        </span>
      ))}
    </section>
  );
}

export function LinhaSecao({ titulo, icone, children, teste }: { titulo: string; icone: NomeIcone; children: ReactNode; teste?: string }) {
  return (
    <section className="linha-secao" data-teste={teste}>
      <h3>
        <Icone nome={icone} /> {titulo}
      </h3>
      <div className="corpo">{children}</div>
    </section>
  );
}

export function Recolhivel({ titulo, icone, children, aberto, teste, extra }: { titulo: string; icone: NomeIcone; children?: ReactNode; aberto?: boolean; teste?: string; extra?: ReactNode }) {
  return (
    <details className="recolhivel" open={aberto} data-teste={teste}>
      <summary>
        <Icone nome={icone} /> <span>{titulo}</span>
        {extra}
        <span className="chev" aria-hidden="true">
          ⌄
        </span>
      </summary>
      {children && <div className="corpo">{children}</div>}
    </details>
  );
}

/**
 * D2/D3: IE e e-mail do documento. "Não informada na data" (havia cadastro, campo vazio) é diferente de "sem
 * evidência histórica" (não há registro do cadastro anterior ao Pedido) — o cadastro atual nunca é usado como histórico.
 */
export function TextoHistorico({ rotulo, v, dataReferencia, teste }: { rotulo: 'IE' | 'E-mail'; v: ValorHistorico; dataReferencia: string; teste?: string }) {
  if (v.estado === 'nao_se_aplica') return null;
  const naData = v.fonte === 'auditoria' ? ` no cadastro em ${dataBr(dataReferencia)}` : '';
  const ajuda =
    v.fonte === 'auditoria' ? (
      <Ajuda titulo={`${rotulo} na data do Pedido`}>{`Pedido anterior ao registro de ${rotulo === 'IE' ? 'IE' : 'e-mail'} no próprio Pedido: o valor foi reconstituído pela auditoria do cadastro na data em que o Pedido foi gerado (${dataBr(dataReferencia)}). Sem registro do cadastro anterior a essa data, não há evidência histórica e nada é mostrado como se fosse histórico.`}</Ajuda>
    ) : null;
  const atual = v.atual ? <span className="sec"> (atual no cadastro: {v.atual})</span> : null;
  if (v.estado === 'sem_evidencia')
    return (
      <span className="sem-evidencia" data-teste={teste}>
        {rotulo}: sem evidência histórica para a data do Pedido{ajuda}
      </span>
    );
  if (v.estado === 'nao_informada')
    return (
      <span className="ausente" data-teste={teste}>
        {rotulo} {rotulo === 'IE' ? 'não informada' : 'não informado'}{naData}
        {ajuda}
        {atual}
      </span>
    );
  return (
    <span data-teste={teste}>
      {rotulo === 'IE' ? `IE ${v.valor}` : v.valor}
      {v.situacao && <span className="sec"> · {v.situacao}</span>}
      {v.fonte === 'auditoria' && <span className="sec"> (conforme cadastro em {dataBr(dataReferencia)})</span>}
      {ajuda}
      {atual}
    </span>
  );
}

/** Junta partes de uma linha com o separador "·", ignorando as vazias. */
export function Junta({ partes }: { partes: ReactNode[] }) {
  const xs = partes.filter((x) => x !== null && x !== undefined && x !== false && x !== '');
  return (
    <>
      {xs.map((x, i) => (
        <span key={i}>
          {i > 0 && <span className="div"> · </span>}
          {x}
        </span>
      ))}
    </>
  );
}

/** Valores empilhados num mesmo campo (ex.: contato, e-mail e telefone). */
export function Empilha({ itens }: { itens: ReactNode[] }) {
  return (
    <>
      {itens.map((x, i) => (
        <span key={i} className="linha-empilhada">
          {x}
        </span>
      ))}
    </>
  );
}

/** Versão em texto (documentos/PDF) do valor histórico — mesma regra da tela (D2/D3). */
export function textoHistorico(v: ValorHistorico, dataReferencia: string, feminino = true): string {
  if (v.estado === 'nao_se_aplica') return '—';
  if (v.estado === 'sem_evidencia') return 'sem evidência histórica para a data do Pedido';
  if (v.estado === 'nao_informada') return `não ${feminino ? 'informada' : 'informado'}${v.fonte === 'auditoria' ? ` no cadastro em ${dataBr(dataReferencia)}` : ''}`;
  return `${v.valor}${v.situacao ? ` · ${v.situacao}` : ''}${v.fonte === 'auditoria' ? ` (cadastro em ${dataBr(dataReferencia)})` : ''}`;
}
